//usage : < tr-input type="number" cfgOnChange trcad-param="main:numParts" onChange="inputChanged" postConfig="" cooloff="1000"></tr-input>

import { devlog } from "../../logic/utils.ts";

interface TrInputConfig {
  type: string;
  cfgOnChange?: boolean;
  'trcad-param'?: string;
  onChange?: string;
  postConfig?: string;
  cooloff?: number;
}

/**
 * Input component that handles parameter changes and configuration
 * Supports number inputs with optional change handlers and cooldown periods
 */
export class TrInput extends HTMLElement {
  private lastTimeout?: number;
  private inputEl: HTMLInputElement;

  static get observedAttributes(): string[] {
    return ['type', 'cfgOnChange', 'trcad-param', 'onChange', 'postConfig', 'cooloff'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Create input element
    this.inputEl = document.createElement('input');
    
    // Set up styles
    const style = document.createElement('style');
    style.textContent = `
      input {
        padding: 4px 8px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 14px;
      }
    `;

    this.shadowRoot?.appendChild(style);
    this.shadowRoot?.appendChild(this.inputEl);

    // Bind event handlers
    this.handleInput = this.handleInput.bind(this);
  }

  connectedCallback(): void {
    const inputType = this.getAttribute('type') ?? 'text';
    this.inputEl.type = inputType;
    this.inputEl.className = `${inputType}`; // Add type-based class
    this.inputEl.addEventListener('input', this.handleInput);
  }

  disconnectedCallback(): void {
    this.inputEl.removeEventListener('input', this.handleInput);
  }

  private handleInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    const value = target.value;
    const cooloff = parseInt(this.getAttribute('cooloff') ?? '0');
    // devlog("Input event triggered", value);
    // Clear existing timeout
    if (this.lastTimeout) {
      window.clearTimeout(this.lastTimeout);
    }

    // Set new timeout if cooloff specified
    if (cooloff > 0) {
      this.lastTimeout = window.setTimeout(() => {
        this.dispatchChange(value);
      }, cooloff);
    } else {
      this.dispatchChange(value);
    }
  }

  private dispatchChange(value: string): void {
    // Handle trcad-param binding
    const paramPath = this.getAttribute('trcad-param');
    
    if (paramPath) {
      devlog("Dispatching change event", value, paramPath);
      const [context, param] = paramPath.split(':');
      // Dispatch custom event for parameter change
      this.dispatchEvent(new CustomEvent('param-change', {
        detail: {
          // context,
          // param,
          paramPath,
          value: this.getAttribute('type') === 'number' ? parseFloat(value) : value
        },
        bubbles: true,
        composed: true
      }));
    }
    else {
      console.warn("No trcad-param attribute found on tr-input");
    }

    // Handle onChange callback
    const onChangeHandler = this.getAttribute('onChange');
    if (onChangeHandler) {
      const event = new CustomEvent(onChangeHandler, {
        detail: { value },
        bubbles: true,
        composed: true
      });
      this.dispatchEvent(event);
    }
  }
}
// devlog("Registering tr-input")
if (!customElements.get('tr-input')) {
  customElements.define('tr-input', TrInput);
} else {
  console.warn("tr-input already defined");
}
